import { useEffect, useMemo, useRef, type MutableRefObject } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { resolveBurstDustColor, resolveLandingDustColor } from '../../materials/appearanceDescriptor';
import { getAppearanceDescriptor } from '../../materials/MaterialRegistry';
import type { DemolitionImpactBurst, ShardParticleSpawn } from '../../structures/demolition/demolitionPhysics';
import type { DemolitionImpactTracePayload } from '../../structures/demolition/demolitionEventRing';
import type { ProfileId } from '../../services/destructionProfiles';
import type { DemolitionVisualState } from '../presentation/effectTier';
import { tierDebrisMultiplier, tierDustMultiplier } from '../presentation/effectTier';
import {
  BURST_LIFE,
  CANNON_DUST_SPREAD_RAD,
  DEBRIS_FAMILIES,
  DEBRIS_GROUND_FRICTION,
  DEBRIS_LIFE,
  DEBRIS_RESTITUTION,
  DEBRIS_SHARD_VARIANTS,
  DUST_APRON_HEIGHT_MAX,
  DUST_APRON_LIFE,
  DUST_APRON_OUT_MIN,
  DUST_APRON_ROOM_SPAN,
  DUST_APRON_WALL_SPAN,
  DUST_CLAY_LUMINANCE,
  DUST_LANDING_ALPHA,
  DUST_LANDING_COUNT,
  DUST_LANDING_LIFE,
  DUST_LIFE,
  DUST_MORTAR_FRACTION,
  DUST_MORTAR_LUMINANCE,
  PLUME_LIFE,
  SHOCKWAVE_LIFE,
  STREAK_LIFE,
  DUST_WALL_OFFSET,
  DEFAULT_WALL_HEIGHT,
  PLUME_MAX_HEIGHT_MUL,
  burstDustSpeed,
  coneDirection,
  coolMortarRgb,
  debrisFamilyOf,
  debrisLifeScale,
  debrisPoolLayout,
  dustApparentScale,
  dustCardScale,
  dustAlphaEnvelope,
  dustClusterCopies,
  dustClusterScale,
  dustFloorAlphaMul,
  dustFloorScaleMul,
  dustLifeScale,
  dustScaleLinkedAlpha,
  effectiveDustLingerScale,
  eventParticleSeed,
  hash01,
  plumeBirthSpeed,
  saturateClayRgb,
  hexToRgb,
  isRubbleApronShape,
  longTailScale,
  orthonormalBasis,
  pulverizedDustBudget,
  randomUnitQuat,
  scaleCount,
  scaleFromVolume,
  stepDebrisMotion,
  stepDustMotion,
  towardLuminance,
  twoToneBurstBias,
  weaponBurstShape,
  type DebrisFamily,
} from './impactFxMath';
import { createMasonryShardGeometry, createSplinterGeometry } from './debrisShards';
import { createSoftDustMaterial, syncSoftParticleUniforms, useSceneDepthTexture } from './softParticles';


export interface DemolitionFxController {
  spawnShards: (shards: ShardParticleSpawn[]) => void;
  spawnShockwave: (
    x: number,
    y: number,
    z: number,
    radius: number,
    sequence: number,
    material: ProfileId,
  ) => void;
  spawnImpactBurst: (burst: DemolitionImpactBurst, sequence: number) => void;
  spawnLandingPuff: (
    x: number,
    y: number,
    z: number,
    sequence: number,
    material: ProfileId,
  ) => void;
  spawnTraceReleases: (payload: DemolitionImpactTracePayload, sequence: number) => void;
  spawnPulverizedCloud: (
    x: number,
    y: number,
    z: number,
    normal: { x: number; y: number; z: number },
    pulverizedVolume: number,
    sequence: number,
    material: ProfileId,
  ) => void;
  liveCount: () => number;
  /** Offscreen one-shot so first-hit programs/JIT land during load. */
  warm: () => void;
}

interface DebrisParticle {
  active: boolean;
  age: number;
  life: number;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  wx: number;
  wy: number;
  wz: number;
  qx: number;
  qy: number;
  qz: number;
  qw: number;
  sx: number;
  sy: number;
  sz: number;
  gravity: number;
  restitution: number;
  friction: number;
  alignVelocity: boolean;
  settled: boolean;
  onGround: boolean;
  stillTime: number;
}

interface DustParticle {
  active: boolean;
  age: number;
  life: number;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  scale: number;
  settleSpeed: number;
  r: number;
  g: number;
  b: number;
  nx: number;
  ny: number;
  nz: number;
  groundAligned: boolean;
  groundHugging: boolean;
  applyUpdraft: boolean;
  alpha: number;
  landing: boolean;
  apron: boolean;
  roll: number;
  maxY: number;
}

interface Shockwave {
  active: boolean;
  age: number;
  x: number;
  y: number;
  z: number;
  radius: number;
  normal: { x: number; y: number; z: number };
}

interface ImpactBurst {
  active: boolean;
  age: number;
  x: number;
  y: number;
  z: number;
  radius: number;
  radial: number;
  axial: number;
  normal: { x: number; y: number; z: number };
}

function emptyDebris(): DebrisParticle {
  return {
    active: false,
    age: 0,
    life: DEBRIS_LIFE,
    x: 0,
    y: 0,
    z: 0,
    vx: 0,
    vy: 0,
    vz: 0,
    wx: 0,
    wy: 0,
    wz: 0,
    qx: 0,
    qy: 0,
    qz: 0,
    qw: 1,
    sx: 0,
    sy: 0,
    sz: 0,
    gravity: 9.8,
    restitution: 0.08,
    friction: 0.42,
    alignVelocity: false,
    settled: false,
    onGround: false,
    stillTime: 0,
  };
}

function emptyDust(): DustParticle {
  return {
    active: false,
    age: 0,
    life: DUST_LIFE,
    x: 0,
    y: 0,
    z: 0,
    vx: 0,
    vy: 0,
    vz: 0,
    scale: 0,
    settleSpeed: 0.28,
    r: 1,
    g: 1,
    b: 1,
    nx: 0,
    ny: 1,
    nz: 0,
    groundAligned: false,
    groundHugging: false,
    applyUpdraft: false,
    alpha: 1,
    landing: false,
    apron: false,
    roll: 0,
    maxY: DEFAULT_WALL_HEIGHT,
  };
}

function debrisMeshIndex(family: DebrisFamily, variant: number): number {
  return DEBRIS_FAMILIES.indexOf(family) * DEBRIS_SHARD_VARIANTS + variant;
}

function tintRgb(hex: string, seed: number): { r: number; g: number; b: number } {
  const rgb = hexToRgb(hex);
  const j = (hash01(seed) - 0.5) * 0.14;
  return {
    r: Math.min(1, Math.max(0.08, rgb.r + j)),
    g: Math.min(1, Math.max(0.08, rgb.g + j)),
    b: Math.min(1, Math.max(0.08, rgb.b + j)),
  };
}

export function ImpactFx({
  fxRef,
  visualState,
}: {
  fxRef: MutableRefObject<DemolitionFxController | null>;
  visualState: DemolitionVisualState;
}) {
  const debrisCount = visualState.debrisPoolSize;
  const dustCount = visualState.dustPoolSize;
  const streakCount = visualState.streakPoolSize;
  const shockCount = visualState.shockwavePoolSize;
  const burstCount = visualState.burstPoolSize;
  const dustMul = tierDustMultiplier(visualState.tier);
  const debrisMul = tierDebrisMultiplier(visualState.tier);
  const { camera, gl } = useThree();
  const depthTexture = useSceneDepthTexture(visualState.mounted);
  const drawingSize = useMemo(() => new THREE.Vector2(), []);
  const colorsReady = useRef(false);

  const { perVariant } = debrisPoolLayout(debrisCount);
  const debrisMeshCount = DEBRIS_FAMILIES.length * DEBRIS_SHARD_VARIANTS;
  const debrisMeshes = useRef<(THREE.InstancedMesh | null)[]>(Array.from({ length: debrisMeshCount }, () => null));
  const streakMesh = useRef<THREE.InstancedMesh>(null);
  const dustMesh = useRef<THREE.InstancedMesh>(null);
  const debrisPools = useRef<DebrisParticle[][]>(
    Array.from({ length: debrisMeshCount }, () => Array.from({ length: perVariant }, emptyDebris)),
  );
  const streaks = useRef<DebrisParticle[]>(Array.from({ length: streakCount }, emptyDebris));
  const dust = useRef<DustParticle[]>(Array.from({ length: dustCount }, emptyDust));
  const nextDebris = useRef<number[]>(Array.from({ length: debrisMeshCount }, () => 0));
  const nextStreak = useRef(0);
  const nextDust = useRef(0);
  const debrisDummy = useMemo(() => new THREE.Object3D(), []);
  const dustDummy = useMemo(() => new THREE.Object3D(), []);
  const velocityAxis = useMemo(() => new THREE.Vector3(0, 0, 1), []);
  const velocityDir = useMemo(() => new THREE.Vector3(), []);
  const colorWork = useMemo(() => new THREE.Color(), []);
  const debrisQuat = useMemo(() => new THREE.Quaternion(), []);

  const shockwaves = useRef<Shockwave[]>(
    Array.from({ length: shockCount }, () => ({
      active: false,
      age: 0,
      x: 0,
      y: 0,
      z: 0,
      radius: 1.6,
      normal: { x: 0, y: 1, z: 0 },
    })),
  );
  const shockNext = useRef(0);
  const shockGroup = useRef<THREE.Group>(null);
  const bursts = useRef<ImpactBurst[]>(
    Array.from({ length: burstCount }, () => ({
      active: false,
      age: 0,
      x: 0,
      y: 0,
      z: 0,
      radius: 0.2,
      radial: 1,
      axial: 1,
      normal: { x: 0, y: 0, z: 1 },
    })),
  );
  const burstNext = useRef(0);
  const burstGroup = useRef<THREE.Group>(null);
  const ringUp = useMemo(() => new THREE.Vector3(0, 0, 1), []);
  const ringNormal = useMemo(() => new THREE.Vector3(), []);
  const groundQuat = useMemo(
    () => new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), -Math.PI / 2),
    [],
  );

  const burstGeometry = useMemo(() => {
    const vertices: number[] = [];
    const spokes = 9;
    for (let i = 0; i < spokes; i += 1) {
      const angle = (i / spokes) * Math.PI * 2;
      vertices.push(0, 0, 0, Math.cos(angle), Math.sin(angle), 0);
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    return geometry;
  }, []);
  const burstCoreGeometry = useMemo(() => new THREE.CircleGeometry(0.16, 16), []);

  // Two-tone is baked as vertex colors on each family geometry. Do not set
  // vertexColors + setColorAt together — missing instanceColor would go black.
  const debrisMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: '#ffffff',
        roughness: 0.9,
        metalness: 0.02,
        vertexColors: true,
      }),
    [],
  );
  const debrisGeometries = useMemo(
    () =>
      DEBRIS_FAMILIES.flatMap((family) => {
        const appearance = getAppearanceDescriptor(family);
        const splinters = appearance.particleStyle.splinters === true;
        return ([0, 1, 2] as const).map((variant) =>
          splinters
            ? createSplinterGeometry(variant, appearance.fragment.outer, appearance.fragment.interior)
            : createMasonryShardGeometry(variant, appearance.fragment.outer, appearance.fragment.interior),
        );
      }),
    [],
  );
  const streakMat = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        color: '#ffffff',
        transparent: true,
        opacity: 0.78,
        depthWrite: false,
      }),
    [],
  );
  const dustTexture = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const context = canvas.getContext('2d');
    if (context) {
      // Lumpy puff, not a shaded sphere: several overlapping soft blobs plus a
      // mild darker edge so pale dust still separates from a lit concrete wall.
      context.clearRect(0, 0, 64, 64);
      // Offset, uneven blobs — no single centered radial that reads as a plate.
      const blobs: Array<[number, number, number, number]> = [
        [18, 26, 17, 0.22],
        [42, 20, 14, 0.18],
        [40, 44, 16, 0.15],
        [16, 46, 11, 0.11],
      ];
      for (const [cx, cy, radius, alpha] of blobs) {
        const gradient = context.createRadialGradient(cx, cy, 1, cx, cy, radius);
        gradient.addColorStop(0, `rgba(255,255,255,${alpha})`);
        gradient.addColorStop(0.32, `rgba(255,255,255,${alpha * 0.38})`);
        gradient.addColorStop(0.62, `rgba(255,255,255,${alpha * 0.08})`);
        gradient.addColorStop(1, 'rgba(255,255,255,0)');
        context.fillStyle = gradient;
        context.fillRect(0, 0, 64, 64);
      }
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
  }, []);
  const dustMat = useMemo(() => createSoftDustMaterial(dustTexture), [dustTexture]);
  const dustGeometry = useMemo(() => {
    const geometry = new THREE.CircleGeometry(1, 18);
    geometry.setAttribute(
      'aPuffAlpha',
      new THREE.InstancedBufferAttribute(new Float32Array(Math.max(dustCount, 1)).fill(1), 1),
    );
    return geometry;
  }, [dustCount]);
  const ringMaterials = useMemo(
    () =>
      Array.from({ length: shockCount }, () =>
        new THREE.MeshBasicMaterial({
          color: '#ff6a3d',
          transparent: true,
          opacity: 0.85,
          side: THREE.DoubleSide,
          depthWrite: false,
        }),
      ),
    [shockCount],
  );
  const burstLineMaterials = useMemo(
    () =>
      Array.from({ length: burstCount }, () =>
        new THREE.LineBasicMaterial({
          color: '#bd916d',
          transparent: true,
          opacity: 0.68,
          depthWrite: false,
        }),
      ),
    [burstCount],
  );
  const burstCoreMaterials = useMemo(
    () =>
      Array.from({ length: burstCount }, () =>
        new THREE.MeshBasicMaterial({
          color: '#b78a63',
          transparent: true,
          opacity: 0.42,
          depthWrite: false,
          side: THREE.DoubleSide,
        }),
      ),
    [burstCount],
  );

  useEffect(() => {
    const takeDebris = (family: DebrisFamily, variant: number): DebrisParticle | null => {
      if (debrisCount <= 0) return null;
      const meshIndex = debrisMeshIndex(family, variant);
      const pool = debrisPools.current[meshIndex];
      if (!pool || pool.length === 0) return null;
      const i = nextDebris.current[meshIndex] % pool.length;
      nextDebris.current[meshIndex] = i + 1;
      return pool[i];
    };
    const takeStreak = (): DebrisParticle | null => {
      if (streakCount <= 0) return null;
      const i = nextStreak.current % streakCount;
      nextStreak.current = i + 1;
      return streaks.current[i];
    };
    const takeDust = (): DustParticle | null => {
      if (dustCount <= 0) return null;
      const i = nextDust.current % dustCount;
      nextDust.current = i + 1;
      return dust.current[i];
    };

    const writeStreakColor = (slot: number, r: number, g: number, b: number) => {
      const mesh = streakMesh.current;
      if (!mesh) return;
      colorWork.setRGB(r, g, b);
      mesh.setColorAt(slot, colorWork);
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    };
    const writeDustColor = (slot: number, r: number, g: number, b: number) => {
      const mesh = dustMesh.current;
      if (!mesh) return;
      colorWork.setRGB(r, g, b);
      mesh.setColorAt(slot, colorWork);
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    };
    const writeDustAlpha = (slot: number, alpha: number) => {
      const mesh = dustMesh.current;
      if (!mesh) return;
      const attr = mesh.geometry.getAttribute('aPuffAlpha') as THREE.InstancedBufferAttribute | undefined;
      if (!attr) return;
      attr.setX(slot, alpha);
      attr.needsUpdate = true;
    };

    const spawnDustParticle = (opts: {
      x: number;
      y: number;
      z: number;
      vx: number;
      vy: number;
      vz: number;
      scale: number;
      life: number;
      settleSpeed: number;
      color: string;
      seed: number;
      nx: number;
      ny: number;
      nz: number;
      groundAligned?: boolean;
      applyUpdraft?: boolean;
      alpha?: number;
      landing?: boolean;
      apron?: boolean;
      luminanceTarget?: number;
      matchLuminance?: boolean;
      roll?: number;
      maxY?: number;
      linkScaleAlpha?: boolean;
    }) => {
      const p = takeDust();
      if (!p) return;
      const slot = (nextDust.current - 1 + dustCount) % dustCount;
      const tinted = tintRgb(opts.color, opts.seed + 19);
      const rgb = opts.matchLuminance
        ? (opts.luminanceTarget === DUST_CLAY_LUMINANCE
            ? saturateClayRgb(tinted)
            : coolMortarRgb(tinted))
        : towardLuminance(tinted);
      p.active = true;
      p.age = 0;
      p.life = opts.life;
      p.x = opts.x;
      p.y = opts.y;
      p.z = opts.z;
      p.vx = opts.vx;
      p.vy = opts.vy;
      p.vz = opts.vz;
      p.scale = opts.scale;
      p.settleSpeed = opts.settleSpeed;
      p.r = rgb.r;
      p.g = rgb.g;
      p.b = rgb.b;
      p.nx = opts.nx;
      p.ny = opts.ny;
      p.nz = opts.nz;
      p.groundAligned = opts.groundAligned === true;
      p.groundHugging = false;
      p.applyUpdraft = opts.applyUpdraft === true;
      p.alpha = opts.linkScaleAlpha === false
        ? (opts.alpha ?? 1)
        : dustScaleLinkedAlpha(opts.alpha ?? 1, opts.scale);
      p.landing = opts.landing === true;
      p.apron = opts.apron === true;
      p.roll = opts.roll ?? hash01(opts.seed + 37) * Math.PI * 2;
      p.maxY = opts.maxY ?? (opts.applyUpdraft
        ? DEFAULT_WALL_HEIGHT * PLUME_MAX_HEIGHT_MUL
        : DEFAULT_WALL_HEIGHT);
      writeDustColor(slot, rgb.r, rgb.g, rgb.b);
      writeDustAlpha(slot, p.alpha);
    };

    const spawnDustPuff = (opts: Parameters<typeof spawnDustParticle>[0]) => {
      const copies = dustClusterCopies(opts.scale, hash01(opts.seed + 41));
      for (let c = 0; c < copies; c += 1) {
        const cs = eventParticleSeed(opts.seed, opts.x, opts.y, opts.z, c, 211);
        const scale = copies === 1 ? opts.scale : dustClusterScale(opts.scale, hash01(cs + 2));
        const jitter = copies === 1 ? 0 : 0.08 + hash01(cs + 3) * 0.12;
        const yaw = hash01(cs + 4) * Math.PI * 2;
        spawnDustParticle({
          ...opts,
          x: opts.x + Math.cos(yaw) * jitter,
          y: opts.y + (hash01(cs + 5) - 0.5) * jitter * 0.45,
          z: opts.z + Math.sin(yaw) * jitter,
          vx: opts.vx + (hash01(cs + 6) - 0.5) * 0.08,
          vz: opts.vz + (hash01(cs + 7) - 0.5) * 0.08,
          scale,
          seed: opts.seed + c * 17,
          roll: hash01(cs + 8) * Math.PI * 2,
        });
      }
    };

    const spawnDebrisParticle = (opts: {
      x: number;
      y: number;
      z: number;
      vx: number;
      vy: number;
      vz: number;
      sx: number;
      sy: number;
      sz: number;
      life: number;
      gravity: number;
      spin: number;
      alignVelocity: boolean;
      material: ProfileId;
      seed: number;
    }) => {
      const family = debrisFamilyOf(opts.material);
      const variant = Math.floor(hash01(opts.seed + 11) * DEBRIS_SHARD_VARIANTS) as 0 | 1 | 2;
      const p = takeDebris(family, variant);
      if (!p) return;
      const spinMag = (2.8 + hash01(opts.seed + 13) * 9.5) * opts.spin;
      const axis = {
        x: hash01(opts.seed + 14) - 0.5,
        y: hash01(opts.seed + 15) - 0.5,
        z: hash01(opts.seed + 16) - 0.5,
      };
      const axisLen = Math.hypot(axis.x, axis.y, axis.z) || 1;
      const q = opts.alignVelocity
        ? { qx: 0, qy: 0, qz: 0, qw: 1 }
        : randomUnitQuat(hash01(opts.seed + 17), hash01(opts.seed + 18), hash01(opts.seed + 19));
      p.active = true;
      p.age = 0;
      p.life = opts.life;
      p.x = opts.x;
      p.y = opts.y;
      p.z = opts.z;
      p.vx = opts.vx;
      p.vy = opts.vy;
      p.vz = opts.vz;
      p.wx = (axis.x / axisLen) * spinMag;
      p.wy = (axis.y / axisLen) * spinMag;
      p.wz = (axis.z / axisLen) * spinMag;
      p.qx = q.qx;
      p.qy = q.qy;
      p.qz = q.qz;
      p.qw = q.qw;
      p.sx = opts.sx;
      p.sy = opts.sy;
      p.sz = opts.sz;
      p.gravity = opts.gravity;
      p.restitution = DEBRIS_RESTITUTION[family];
      p.friction = DEBRIS_GROUND_FRICTION[family];
      p.alignVelocity = opts.alignVelocity;
      p.settled = false;
      p.onGround = opts.y <= 0.04;
      p.stillTime = 0;
    };

    const spawnStreakParticle = (opts: {
      x: number;
      y: number;
      z: number;
      vx: number;
      vy: number;
      vz: number;
      length: number;
      color: string;
      seed: number;
    }) => {
      const p = takeStreak();
      if (!p) return;
      const slot = (nextStreak.current - 1 + streakCount) % streakCount;
      const rgb = tintRgb(opts.color, opts.seed + 29);
      p.active = true;
      p.age = 0;
      p.life = STREAK_LIFE;
      p.x = opts.x;
      p.y = opts.y;
      p.z = opts.z;
      p.vx = opts.vx;
      p.vy = opts.vy;
      p.vz = opts.vz;
      p.sx = 0.012;
      p.sy = 0.012;
      p.sz = opts.length;
      p.gravity = 1.2;
      p.wx = 0;
      p.wy = 0;
      p.wz = 0;
      p.qx = 0;
      p.qy = 0;
      p.qz = 0;
      p.qw = 1;
      p.alignVelocity = true;
      p.settled = false;
      p.onGround = false;
      p.restitution = 0;
      p.friction = 0.4;
      writeStreakColor(slot, rgb.r, rgb.g, rgb.b);
    };

    const spawnDirectedDust = (
      x: number,
      y: number,
      z: number,
      normal: { x: number; y: number; z: number },
      radius: number,
      count: number,
      sequence: number,
      material: ProfileId,
      shape = weaponBurstShape('cannon'),
      lifeScale = 1,
      kind: 'burst' | 'landing' = 'burst',
    ) => {
      const appearance = getAppearanceDescriptor(material);
      const basis = orthonormalBasis(normal);
      const heavy = appearance.particleStyle.heavyFines === true;
      const n = heavy ? Math.max(1, Math.round(count * 0.9)) : count;
      const upward = basis.ny > 0.75;
      const wallBurst = !upward && kind === 'burst';
      const rubbleApron = wallBurst && isRubbleApronShape(shape);
      const cannonSpread = wallBurst
        && Math.abs(shape.spreadRad - (38 * Math.PI) / 180) < 1e-4
        ? CANNON_DUST_SPREAD_RAD
        : shape.spreadRad;
      const twoTone = appearance.dust.secondaryColor !== undefined;
      const mortarN = twoTone ? Math.round(n * DUST_MORTAR_FRACTION) : 0;
      const family = debrisFamilyOf(material);
      const linger = effectiveDustLingerScale(family, appearance.dust.lingerScale);
      for (let k = 0; k < n; k += 1) {
        const seed = eventParticleSeed(sequence, x, y, z, k, 11);
        const brickMortar = twoTone && k < mortarN;
        const tone = twoTone && !rubbleApron ? twoToneBurstBias(brickMortar) : null;
        const spreadRad = tone ? tone.spreadRad : cannonSpread;
        const along = tone ? tone.along : shape.along;
        const dir = coneDirection(
          basis,
          hash01(seed) * Math.PI * 2,
          hash01(seed + 1),
          spreadRad,
          along,
        );
        const speedMul = (tone ? tone.speedMul : 1) * (heavy ? 0.55 : 1);
        const speed = burstDustSpeed(hash01(seed + 3), speedMul, shape.speedMul)
          * (0.82 + Math.min(0.55, radius) * 0.5);
        const color = resolveBurstDustColor(appearance, twoTone && !brickMortar);
        const lowSheet = rubbleApron;
        const scaleKind = heavy
          ? 'heavy'
          : kind === 'landing'
            ? 'landing'
            : lowSheet
              ? 'apron'
              : 'burst';
        const scale = dustCardScale(hash01(seed + 4), scaleKind) * (tone ? tone.scaleMul : 1);
        const lift = heavy ? 0.14 : DUST_WALL_OFFSET;
        const throwDist = wallBurst
          ? (heavy
              ? 0.04 + hash01(seed + 6) * 0.08
              : rubbleApron
                ? 0
                : tone && !brickMortar
                  ? 0.7 + hash01(seed + 6) * 0.95
                  : 0.42 + hash01(seed + 6) * 1.05)
          : 0;
        let px = x + basis.nx * lift + dir.x * throwDist;
        let py = y + basis.ny * lift + dir.y * throwDist;
        let pz = z + basis.nz * lift + dir.z * throwDist;
        let vx = dir.x * speed;
        let vy = dir.y * speed + (heavy ? 0.05 : 0);
        let vz = dir.z * speed;
        if (lowSheet) {
          const theta = hash01(seed + 12) * Math.PI * 2;
          const radial = Math.sqrt(hash01(seed + 13));
          const alongWall = Math.cos(theta) * DUST_APRON_WALL_SPAN * radial;
          const outRoom = DUST_APRON_OUT_MIN + Math.abs(Math.sin(theta)) * DUST_APRON_ROOM_SPAN * radial;
          px = x + basis.ux * alongWall + basis.nx * outRoom;
          py = 0.05 + hash01(seed + 14) * DUST_APRON_HEIGHT_MAX;
          pz = z + basis.uz * alongWall + basis.nz * outRoom;
          const alongSign = alongWall >= 0 ? 1 : -1;
          vx = basis.ux * alongSign * speed * 0.85 + basis.nx * speed * 0.3;
          vy = 0.015 + hash01(seed + 7) * 0.04;
          vz = basis.uz * alongSign * speed * 0.85 + basis.nz * speed * 0.3;
        } else if (tone && brickMortar) {
          const sideSign = hash01(seed + 12) < 0.5 ? -1 : 1;
          const side = sideSign * (0.42 + hash01(seed + 18) * 0.85);
          const liftAlong = (hash01(seed + 15) - 0.35) * 0.85;
          px = x + basis.ux * side + basis.nx * 0.24;
          py = y + liftAlong;
          pz = z + basis.uz * side + basis.nz * 0.24;
        } else if (tone && !brickMortar) {
          const out = 0.55 + hash01(seed + 16) * 0.85;
          px = x + basis.nx * out + dir.x * 0.12;
          py = y + dir.y * 0.12;
          pz = z + basis.nz * out + dir.z * 0.12;
        }
        let cnx = basis.nx;
        let cny = basis.ny;
        let cnz = basis.nz;
        if (upward) {
          const tilt = 0.5 + hash01(seed + 9) * 0.35;
          const yaw = hash01(seed + 10) * Math.PI * 2;
          cnx = Math.cos(yaw) * (1 - tilt);
          cny = tilt;
          cnz = Math.sin(yaw) * (1 - tilt);
        }
        const cpuLife = lowSheet
          ? DUST_APRON_LIFE * linger
          : DUST_LIFE * linger * shape.lifeMul * lifeScale;
        spawnDustPuff({
          x: px,
          y: py,
          z: pz,
          vx,
          vy,
          vz,
          scale,
          life: cpuLife,
          settleSpeed: appearance.dust.settleSpeed * (lowSheet ? 0.55 : 1),
          color,
          seed,
          nx: cnx,
          ny: cny,
          nz: cnz,
          applyUpdraft: false,
          alpha: heavy ? 0.7 : brickMortar ? 0.94 : 1,
          apron: lowSheet,
          matchLuminance: twoTone,
          luminanceTarget: twoTone
            ? (brickMortar ? DUST_MORTAR_LUMINANCE : DUST_CLAY_LUMINANCE)
            : undefined,
          maxY: DEFAULT_WALL_HEIGHT,
        });
      }
      if (family === 'brick' && kind === 'burst') {
        const filmN = 12;
        for (let f = 0; f < filmN; f += 1) {
          const seed = eventParticleSeed(sequence, x, y, z, f, 611);
          const yaw = hash01(seed) * Math.PI * 2;
          const radial = 0.06 + hash01(seed + 1) * 0.18;
          spawnDustPuff({
            x: x + basis.nx * 0.48 + Math.cos(yaw) * radial,
            y: y + (hash01(seed + 2) - 0.4) * 0.28,
            z: z + basis.nz * 0.48 + Math.sin(yaw) * radial,
            vx: (hash01(seed + 3) - 0.5) * 0.08,
            vy: -0.04,
            vz: (hash01(seed + 4) - 0.5) * 0.08,
            scale: 0.20 + hash01(seed + 5) * 0.10,
            life: DUST_LIFE * linger * 1.15,
            settleSpeed: appearance.dust.settleSpeed * 0.35,
            color: resolveBurstDustColor(appearance, false),
            seed,
            nx: basis.nx,
            ny: 0.2,
            nz: basis.nz,
            applyUpdraft: false,
            alpha: 0.72,
            maxY: DEFAULT_WALL_HEIGHT,
          });
        }
      }
    };

    const spawnBurstDebris = (
      x: number,
      y: number,
      z: number,
      normal: { x: number; y: number; z: number },
      radius: number,
      count: number,
      sequence: number,
      material: ProfileId,
      shape = weaponBurstShape('cannon'),
    ) => {
      const appearance = getAppearanceDescriptor(material);
      const basis = orthonormalBasis(normal);
      const splinters = appearance.particleStyle.splinters === true;
      const heavy = appearance.particleStyle.heavyFines === true;
      const n = splinters ? scaleCount(count + 4, debrisMul) : count;
      for (let k = 0; k < n; k += 1) {
        const seed = eventParticleSeed(sequence, x, y, z, k, 41);
        const dir = coneDirection(
          basis,
          hash01(seed) * Math.PI * 2,
          hash01(seed + 1),
          shape.spreadRad,
          shape.along,
        );
        const speed = radius * (3.2 + hash01(seed + 3) * 4.4) * shape.speedMul;
        const tail = longTailScale(hash01(seed + 4), 0.028, heavy ? 0.16 : 0.11);
        const thick = splinters ? tail * 0.34 : tail;
        const length = splinters ? tail * (4.2 + hash01(seed + 6) * 3.6) : tail;
        spawnDebrisParticle({
          x: x + basis.nx * 0.04,
          y: y + basis.ny * 0.04,
          z: z + basis.nz * 0.04,
          vx: dir.x * speed,
          vy: dir.y * speed + 0.4,
          vz: dir.z * speed,
          sx: thick,
          sy: thick,
          sz: length,
          life: DEBRIS_LIFE * shape.lifeMul * (heavy ? 0.7 : 1),
          gravity: heavy ? 14 : splinters ? 7.2 : 9.8,
          spin: splinters ? 0.4 : 1,
          alignVelocity: splinters,
          material,
          seed,
        });
      }
    };

    const spawnPlume = (
      x: number,
      y: number,
      z: number,
      sequence: number,
      material: ProfileId,
      count: number,
    ) => {
      const appearance = getAppearanceDescriptor(material);
      const n = scaleCount(count, dustMul);
      const family = debrisFamilyOf(material);
      const linger = effectiveDustLingerScale(family, appearance.dust.lingerScale);
      const ceiling = DEFAULT_WALL_HEIGHT * PLUME_MAX_HEIGHT_MUL;
      for (let k = 0; k < n; k += 1) {
        const seed = eventParticleSeed(sequence, x, y, z, k, 71);
        const radial = 0.10 + hash01(seed) * 0.38;
        const angle = hash01(seed + 1) * Math.PI * 2;
        const px = x + Math.cos(angle) * radial * 0.35;
        const pz = z + Math.sin(angle) * radial * 0.35;
        const vx = Math.cos(angle) * radial * 0.28;
        const vy = plumeBirthSpeed(hash01(seed + 2));
        const vz = Math.sin(angle) * radial * 0.28;
        const scale = 0.26 + hash01(seed + 4) * 0.08;
        const py0 = Math.min(y - 0.35 + hash01(seed + 5) * 0.22, DEFAULT_WALL_HEIGHT - 0.8);
        spawnDustPuff({
          x: px,
          y: py0,
          z: pz,
          vx,
          vy,
          vz,
          scale,
          life: PLUME_LIFE * linger,
          settleSpeed: appearance.dust.settleSpeed * 0.45,
          color: resolveBurstDustColor(appearance, false),
          seed,
          nx: Math.cos(angle),
          ny: 0.18,
          nz: Math.sin(angle),
          applyUpdraft: true,
          maxY: ceiling,
          alpha: 1,
        });
      }
    };

    fxRef.current = {
      spawnShards(shards: ShardParticleSpawn[]) {
        for (let s = 0; s < shards.length; s += 1) {
          const shard = shards[s];
          const appearance = getAppearanceDescriptor(shard.material);
          const seed = eventParticleSeed(s + 1, shard.x, shard.y, shard.z, s, 3);
          const vol = scaleFromVolume(shard.volume);
          const tail = longTailScale(hash01(seed), 0.55, 1.35);
          const splinters = appearance.particleStyle.splinters === true;
          const base = vol * tail;
          spawnDebrisParticle({
            x: shard.x,
            y: shard.y,
            z: shard.z,
            vx: shard.vx,
            vy: shard.vy,
            vz: shard.vz,
            sx: splinters ? base * 0.32 : base,
            sy: splinters ? base * 0.32 : base,
            sz: splinters ? base * 3.4 : base,
            life: DEBRIS_LIFE,
            gravity: appearance.particleStyle.heavyFines ? 14 : 9.8,
            spin: splinters ? 0.35 : 1,
            alignVelocity: splinters,
            material: shard.material,
            seed,
          });
        }
      },
      spawnShockwave(x, y, z, radius, sequence, material) {
        if (shockCount > 0) {
          const i = shockNext.current % shockCount;
          shockNext.current = i + 1;
          const s = shockwaves.current[i];
          const concussion = radius < 2.05;
          s.active = true;
          s.age = 0;
          s.x = x;
          s.y = y;
          s.z = z;
          s.radius = Math.max(radius * (concussion ? 0.55 : 1), 0.55);
          s.normal = { x: 0, y: 1, z: 0 };
          if (concussion) {
            spawnDirectedDust(
              x,
              y,
              z,
              { x: 0, y: 1, z: 0 },
              radius,
              scaleCount(16, dustMul),
              sequence,
              material,
              weaponBurstShape('concussion'),
            );
          } else {
            spawnPlume(x, y, z, sequence, material, 64);
            spawnDirectedDust(
              x,
              y,
              z,
              { x: 0, y: 1, z: 0 },
              radius,
              scaleCount(26, dustMul),
              sequence,
              material,
              weaponBurstShape('bomb'),
              1.1,
            );
          }
        }
      },
      spawnImpactBurst(burst, sequence) {
        const shape = weaponBurstShape(burst.weapon);
        if (burstCount > 0) {
          const i = burstNext.current % burstCount;
          burstNext.current = i + 1;
          const s = bursts.current[i];
          s.active = true;
          s.age = 0;
          s.x = burst.x;
          s.y = burst.y;
          s.z = burst.z;
          s.radius = burst.radius;
          s.radial = shape.burstRadial;
          s.axial = shape.burstAxial;
          s.normal = { ...burst.normal };
        }
        spawnDirectedDust(
          burst.x,
          burst.y,
          burst.z,
          burst.normal,
          burst.radius,
          scaleCount(shape.dustCount, dustMul),
          sequence,
          burst.material,
          shape,
        );
        spawnBurstDebris(
          burst.x,
          burst.y,
          burst.z,
          burst.normal,
          burst.radius,
          scaleCount(shape.debrisCount, debrisMul),
          sequence,
          burst.material,
          shape,
        );
        if (shape.streakCount > 0) {
          const basis = orthonormalBasis(burst.normal);
          for (let k = 0; k < shape.streakCount; k += 1) {
            const seed = eventParticleSeed(sequence, burst.x, burst.y, burst.z, k, 91);
            const dir = coneDirection(basis, hash01(seed) * Math.PI * 2, hash01(seed + 1) * 0.35, shape.spreadRad, 0.96);
            const speed = burst.radius * (9 + hash01(seed + 3) * 6) * shape.speedMul;
            spawnStreakParticle({
              x: burst.x + basis.nx * 0.05,
              y: burst.y + basis.ny * 0.05,
              z: burst.z + basis.nz * 0.05,
              vx: dir.x * speed,
              vy: dir.y * speed,
              vz: dir.z * speed,
              length: 0.18 + hash01(seed + 4) * 0.22,
              color: getAppearanceDescriptor(burst.material).chips.color,
              seed,
            });
          }
        }
      },
      spawnLandingPuff(x, y, z, sequence, material) {
        if (dustMul <= 0) return;
        // Topology traces fire up to 8 coincident releases. Most must no-op or
        // they stack into one bright floor disc at the crater foot.
        if (hash01(eventParticleSeed(sequence, x, y, z, 0, 17)) > 0.22) return;
        const appearance = getAppearanceDescriptor(material);
        const color = resolveLandingDustColor(appearance);
        const linger = effectiveDustLingerScale(debrisFamilyOf(material), appearance.dust.lingerScale);
        for (let k = 0; k < DUST_LANDING_COUNT; k += 1) {
          const seed = eventParticleSeed(sequence, x, y, z, k, 17);
          const angle = hash01(seed) * Math.PI * 2;
          const radial = 0.28 + hash01(seed + 1) * 0.55;
          const speed = 0.03 + hash01(seed + 3) * 0.06;
          spawnDustPuff({
            x: x + Math.cos(angle) * radial,
            y: 0.08 + hash01(seed + 5) * 0.14,
            z: z + Math.sin(angle) * radial,
            vx: Math.cos(angle) * speed,
            vy: 0.10 + hash01(seed + 6) * 0.16,
            vz: Math.sin(angle) * speed,
            scale: dustCardScale(hash01(seed + 4), 'landing'),
            life: DUST_LANDING_LIFE * linger,
            settleSpeed: appearance.dust.settleSpeed,
            color,
            seed,
            nx: Math.cos(angle),
            ny: 0.62,
            nz: Math.sin(angle),
            groundAligned: false,
            applyUpdraft: false,
            alpha: DUST_LANDING_ALPHA,
            landing: true,
          });
        }
      },
      spawnTraceReleases(payload, sequence) {
        const releases = (
          payload.supportReleases.length > 0 ? payload.supportReleases : payload.directReleases
        ).slice(0, 8);
        const appearance = getAppearanceDescriptor(payload.material);
        const splinters = appearance.particleStyle.splinters === true;
        const linger = effectiveDustLingerScale(debrisFamilyOf(payload.material), appearance.dust.lingerScale);
        for (let i = 0; i < releases.length; i += 1) {
          const release = releases[i];
          const debrisN = scaleCount(splinters ? 2 : 1, debrisMul);
          const dustN = scaleCount(5, dustMul);
          for (let k = 0; k < debrisN; k += 1) {
            const seed = eventParticleSeed(sequence, release.position.x, release.position.y, release.position.z, i * 8 + k, 101);
            const jitter = 0.14;
            const vx = release.vx * (1 + (hash01(seed) - 0.5) * jitter);
            const vy = release.vy * (1 + (hash01(seed + 1) - 0.5) * jitter);
            const vz = release.vz * (1 + (hash01(seed + 2) - 0.5) * jitter);
            const fallback = Math.hypot(vx, vy, vz) < 0.05;
            const tail = longTailScale(hash01(seed + 4), 0.014, 0.06);
            spawnDebrisParticle({
              x: release.position.x,
              y: release.position.y,
              z: release.position.z,
              vx: fallback ? (hash01(seed + 5) - 0.5) * 1.4 : vx,
              vy: fallback ? 0.8 + hash01(seed + 6) * 1.2 : vy,
              vz: fallback ? (hash01(seed + 7) - 0.5) * 1.4 : vz,
              sx: splinters ? tail * 0.3 : tail,
              sy: splinters ? tail * 0.3 : tail,
              sz: splinters ? tail * 3.8 : tail,
              life: DEBRIS_LIFE * 0.85,
              gravity: appearance.particleStyle.heavyFines ? 14 : 9.8,
              spin: splinters ? 0.3 : 1,
              alignVelocity: splinters || Math.hypot(vx, vy, vz) > 0.4,
              material: payload.material,
              seed,
            });
          }
          for (let k = 0; k < dustN; k += 1) {
            const seed = eventParticleSeed(sequence, release.position.x, release.position.y, release.position.z, i * 8 + k, 131);
            const speedScale = 0.35 + hash01(seed) * 0.25;
            spawnDustPuff({
              x: release.position.x,
              y: Math.min(release.position.y, DEFAULT_WALL_HEIGHT - 0.2),
              z: release.position.z,
              vx: release.vx * speedScale,
              vy: Math.min(release.vy * speedScale, 0.12) - 0.05,
              vz: release.vz * speedScale,
              scale: dustCardScale(hash01(seed + 4), 'trace'),
              life: DUST_LIFE * linger * 0.8,
              settleSpeed: appearance.dust.settleSpeed,
              color: resolveBurstDustColor(appearance, false),
              seed,
              nx: 0,
              ny: 1,
              nz: 0,
              applyUpdraft: false,
              maxY: DEFAULT_WALL_HEIGHT,
            });
          }
        }
      },
      spawnPulverizedCloud(x, y, z, normal, pulverizedVolume, sequence, material) {
        if (dustMul <= 0 || !(pulverizedVolume > 1e-8)) return;
        const budget = pulverizedDustBudget(pulverizedVolume, visualState.tier);
        if (budget.count <= 0) return;
        const appearance = getAppearanceDescriptor(material);
        const twoTone = appearance.dust.secondaryColor !== undefined;
        const family = debrisFamilyOf(material);
        const linger = effectiveDustLingerScale(family, appearance.dust.lingerScale) * budget.lingerMul;
        const basis = orthonormalBasis(normal);
        const mortarN = twoTone ? Math.round(budget.count * 0.38) : 0;
        for (let k = 0; k < budget.count; k += 1) {
          const seed = eventParticleSeed(sequence, x, y, z, k, 701);
          const brickMortar = twoTone && k < mortarN;
          const color = resolveBurstDustColor(appearance, twoTone && !brickMortar);
          const yaw = hash01(seed) * Math.PI * 2;
          const core = k < budget.count * 0.3;
          const spread = 0.10 + hash01(seed + 1) * (core ? 0.22 : 0.48 + budget.sizeMul * 0.4);
          const lift = 0.06 + hash01(seed + 2) * (core ? 0.14 : 0.28);
          const face = 0.34 + hash01(seed + 7) * 0.14;
          const px = x + Math.cos(yaw) * spread + basis.nx * face;
          const py = Math.max(0.08, y + lift - 0.04);
          const pz = z + Math.sin(yaw) * spread + basis.nz * face;
          const speed = (0.28 + hash01(seed + 3) * 0.7) * budget.speedMul;
          const vx = Math.cos(yaw) * speed * 0.5 + basis.nx * speed * 0.2;
          const vy = 0.28 * budget.speedMul + hash01(seed + 4) * 0.38;
          const vz = Math.sin(yaw) * speed * 0.5 + basis.nz * speed * 0.2;
          const scale = ((core ? 0.36 : 0.22) + hash01(seed + 5) * (core ? 0.42 : 0.3)) * budget.sizeMul;
          const life = (2.6 + hash01(seed + 6) * 4.8) * linger;
          spawnDustPuff({
            x: px,
            y: py,
            z: pz,
            vx,
            vy,
            vz,
            scale,
            life,
            settleSpeed: appearance.dust.settleSpeed * 0.7,
            color,
            seed,
            nx: basis.nx,
            ny: 0.45,
            nz: basis.nz,
            applyUpdraft: pulverizedVolume > 0.012,
            alpha: twoTone ? (brickMortar ? 0.9 : 0.97) : 0.9,
            linkScaleAlpha: false,
            matchLuminance: twoTone,
            luminanceTarget: twoTone
              ? (brickMortar ? DUST_MORTAR_LUMINANCE : DUST_CLAY_LUMINANCE)
              : undefined,
            maxY: DEFAULT_WALL_HEIGHT * 1.05,
          });
        }
      },
      liveCount() {
        let n = 0;
        for (const pool of debrisPools.current) {
          for (const p of pool) if (p.active) n += 1;
        }
        for (const p of streaks.current) if (p.active) n += 1;
        for (const p of dust.current) if (p.active) n += 1;
        return n;
      },
      warm() {
        const s = shockwaves.current[0];
        if (s) {
          s.active = true;
          s.age = Math.max(0, SHOCKWAVE_LIFE - 0.02);
          s.x = 0;
          s.y = -80;
          s.z = 0;
          s.radius = 0.01;
          s.normal = { x: 0, y: 1, z: 0 };
        }
        const burst = bursts.current[0];
        if (burst) {
          burst.active = true;
          burst.age = Math.max(0, BURST_LIFE - 0.02);
          burst.x = 0;
          burst.y = -80;
          burst.z = 0;
          burst.radius = 0.01;
          burst.radial = 1;
          burst.axial = 1;
          burst.normal = { x: 0, y: 0, z: 1 };
        }
        spawnDustParticle({
          x: 0,
          y: -80,
          z: 0,
          vx: 0,
          vy: 0,
          vz: 0,
          scale: 0.001,
          life: 0.04,
          settleSpeed: 0.2,
          color: '#d8d2c6',
          seed: 1,
          nx: 0,
          ny: 1,
          nz: 0,
          alpha: 0,
        });
      },
    };
    if (import.meta.env.DEV) {
      (window as unknown as { __dustDebug?: () => unknown }).__dustDebug = () => {
        const live = dust.current.filter((p) => p.active);
        return {
          live: live.length,
          samples: live.slice(0, 8).map((p) => ({
            x: +p.x.toFixed(3),
            y: +p.y.toFixed(3),
            z: +p.z.toFixed(3),
            scale: +p.scale.toFixed(3),
            age: +p.age.toFixed(3),
            life: +p.life.toFixed(3),
            apron: p.apron,
            alpha: +p.alpha.toFixed(3),
            r: +p.r.toFixed(3),
            g: +p.g.toFixed(3),
            b: +p.b.toFixed(3),
          })),
        };
      };
    }
    return () => {
      fxRef.current = null;
      if (import.meta.env.DEV) {
        delete (window as unknown as { __dustDebug?: () => unknown }).__dustDebug;
      }
    };
  }, [colorWork, debrisCount, debrisMul, dustCount, dustMul, fxRef, shockCount, burstCount, streakCount, visualState.tier]);

  useEffect(() => {
    return () => {
      debrisMat.dispose();
      streakMat.dispose();
      dustMat.dispose();
      dustTexture.dispose();
      dustGeometry.dispose();
      burstGeometry.dispose();
      burstCoreGeometry.dispose();
      for (const geometry of debrisGeometries) geometry.dispose();
      for (const material of ringMaterials) material.dispose();
      for (const material of burstLineMaterials) material.dispose();
      for (const material of burstCoreMaterials) material.dispose();
    };
  }, [
    burstCoreGeometry,
    burstCoreMaterials,
    burstGeometry,
    burstLineMaterials,
    debrisGeometries,
    debrisMat,
    dustGeometry,
    dustMat,
    dustTexture,
    ringMaterials,
    streakMat,
  ]);

  useFrame((_, rawDt) => {
    const dt = Math.min(Math.max(rawDt, 0), 0.05);
    try {
      if (!colorsReady.current) {
        const tint = (mesh: THREE.InstancedMesh | null, count: number) => {
          if (!mesh || count <= 0) return;
          colorWork.setRGB(0.75, 0.75, 0.72);
          for (let i = 0; i < count; i += 1) mesh.setColorAt(i, colorWork);
          if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
        };
        tint(streakMesh.current, streakCount);
        tint(dustMesh.current, dustCount);
        colorsReady.current = true;
      }
      if (depthTexture) {
        const drawing = gl.getDrawingBufferSize(drawingSize);
        syncSoftParticleUniforms(dustMat, depthTexture, camera, drawing.x, drawing.y);
      }

    const hideInstance = (mesh: THREE.InstancedMesh, i: number) => {
      debrisDummy.scale.setScalar(0);
      debrisDummy.position.set(0, -100, 0);
      debrisDummy.updateMatrix();
      mesh.setMatrixAt(i, debrisDummy.matrix);
    };

    const stepDebrisPool = (mesh: THREE.InstancedMesh | null, pool: DebrisParticle[]) => {
      if (!mesh) return;
      for (let i = 0; i < pool.length; i += 1) {
        const p = pool[i];
        if (!p.active) {
          hideInstance(mesh, i);
          continue;
        }
        p.age += dt;
        if (p.age >= p.life) {
          p.active = false;
          hideInstance(mesh, i);
          continue;
        }
        stepDebrisMotion(p, dt);
        const shrink = debrisLifeScale(p.age, p.life);
        debrisDummy.position.set(p.x, p.y, p.z);
        debrisQuat.set(p.qx, p.qy, p.qz, p.qw);
        debrisDummy.quaternion.copy(debrisQuat);
        debrisDummy.scale.set(
          Math.max(p.sx * shrink, 0.001),
          Math.max(p.sy * shrink, 0.001),
          Math.max(p.sz * shrink, 0.001),
        );
        debrisDummy.updateMatrix();
        mesh.setMatrixAt(i, debrisDummy.matrix);
      }
      mesh.instanceMatrix.needsUpdate = true;
    };

    const stepStreaks = () => {
      const mesh = streakMesh.current;
      if (!mesh) return;
      for (let i = 0; i < streakCount; i += 1) {
        const p = streaks.current[i];
        if (!p.active) {
          hideInstance(mesh, i);
          continue;
        }
        p.age += dt;
        if (p.age >= p.life) {
          p.active = false;
          hideInstance(mesh, i);
          continue;
        }
        p.vx *= 0.98;
        p.vy -= p.gravity * dt;
        p.vz *= 0.98;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.z += p.vz * dt;
        const shrink = 1 - p.age / p.life;
        debrisDummy.position.set(p.x, p.y, p.z);
        velocityDir.set(p.vx, p.vy, p.vz);
        if (velocityDir.lengthSq() < 1e-8) debrisDummy.quaternion.identity();
        else {
          velocityDir.normalize();
          if (velocityDir.distanceToSquared(velocityAxis) < 1e-10) debrisDummy.quaternion.identity();
          else debrisDummy.quaternion.setFromUnitVectors(velocityAxis, velocityDir);
        }
        debrisDummy.scale.set(
          Math.max(p.sx * shrink, 0.001),
          Math.max(p.sy * shrink, 0.001),
          Math.max(p.sz * shrink, 0.001),
        );
        debrisDummy.updateMatrix();
        mesh.setMatrixAt(i, debrisDummy.matrix);
      }
      mesh.instanceMatrix.needsUpdate = true;
    };

    for (let m = 0; m < debrisMeshCount; m += 1) {
      stepDebrisPool(debrisMeshes.current[m], debrisPools.current[m]);
    }
    stepStreaks();

    const dMesh = dustMesh.current;
    if (dMesh) {
      const puffAlphaAttr = dMesh.geometry.getAttribute('aPuffAlpha') as
        | THREE.InstancedBufferAttribute
        | undefined;
      for (let i = 0; i < dustCount; i += 1) {
        const p = dust.current[i];
        if (!p.active) {
          dustDummy.scale.setScalar(0);
          dustDummy.position.set(0, -100, 0);
          dustDummy.updateMatrix();
          dMesh.setMatrixAt(i, dustDummy.matrix);
          continue;
        }
        p.age += dt;
        if (p.age >= p.life) {
          p.active = false;
          dustDummy.scale.setScalar(0);
          dustDummy.position.set(0, -100, 0);
          dustDummy.updateMatrix();
          dMesh.setMatrixAt(i, dustDummy.matrix);
          continue;
        }
        stepDustMotion(p, dt);
        if (p.groundAligned) p.y = 0.028;
        dustDummy.position.set(p.x, p.y, p.z);
        const lifeScale = dustLifeScale(p.age, p.life);
        const viewDist = camera.position.distanceTo(dustDummy.position);
        const hugFloor = p.apron || p.groundHugging || p.landing;
        const floorScale = hugFloor ? 1 : dustFloorScaleMul(p.y);
        const drawn = dustApparentScale(p.scale * lifeScale * floorScale, viewDist, p.y, p.apron);
        if (p.groundAligned) {
          dustDummy.quaternion.copy(groundQuat);
          dustDummy.rotateZ(p.roll);
          dustDummy.scale.set(drawn, drawn, 1);
        } else {
          dustDummy.quaternion.copy(camera.quaternion);
          dustDummy.rotateZ(p.roll);
          dustDummy.scale.setScalar(drawn);
        }
        dustDummy.updateMatrix();
        dMesh.setMatrixAt(i, dustDummy.matrix);
        if (puffAlphaAttr) {
          const floorAlpha = hugFloor ? 1 : dustFloorAlphaMul(p.y);
          puffAlphaAttr.setX(i, p.alpha * dustAlphaEnvelope(p.age, p.life) * floorAlpha);
        }
      }
      if (puffAlphaAttr) puffAlphaAttr.needsUpdate = true;
      dMesh.instanceMatrix.needsUpdate = true;
    }

    const group = shockGroup.current;
    if (group) {
      for (let i = 0; i < shockwaves.current.length; i += 1) {
        const s = shockwaves.current[i];
        const mesh = group.children[i] as THREE.Mesh | undefined;
        if (!mesh) continue;
        if (!s.active) {
          mesh.visible = false;
          continue;
        }
        s.age += dt;
        if (s.age >= SHOCKWAVE_LIFE) {
          s.active = false;
          mesh.visible = false;
          continue;
        }
        const t = s.age / SHOCKWAVE_LIFE;
        const scale = s.radius * (0.25 + t * 1.6);
        mesh.visible = true;
        mesh.position.set(s.x, s.y, s.z);
        ringNormal.set(s.normal.x, s.normal.y, s.normal.z);
        if (ringNormal.lengthSq() < 1e-8) ringNormal.set(0, 1, 0);
        else ringNormal.normalize();
        mesh.quaternion.setFromUnitVectors(ringUp, ringNormal);
        mesh.scale.set(scale, scale, scale);
        const mat = mesh.material as THREE.MeshBasicMaterial;
        mat.opacity = 0.9 * (1 - t);
      }
    }

    const burstRoot = burstGroup.current;
    if (burstRoot) {
      for (let i = 0; i < bursts.current.length; i += 1) {
        const burst = bursts.current[i];
        const root = burstRoot.children[i];
        if (!root) continue;
        if (!burst.active) {
          root.visible = false;
          continue;
        }
        burst.age += dt;
        if (burst.age >= BURST_LIFE) {
          burst.active = false;
          root.visible = false;
          continue;
        }
        const t = burst.age / BURST_LIFE;
        root.visible = true;
        ringNormal.set(burst.normal.x, burst.normal.y, burst.normal.z).normalize();
        root.position.set(
          burst.x + ringNormal.x * 0.05,
          burst.y + ringNormal.y * 0.05,
          burst.z + ringNormal.z * 0.05,
        );
        root.quaternion.setFromUnitVectors(ringUp, ringNormal);
        const grow = burst.radius * (0.55 + t * 1.7);
        root.scale.set(grow * burst.radial, grow * burst.radial, grow * burst.axial);
        const lineObject = root.children[0] as THREE.LineSegments | undefined;
        const coreObject = root.children[1] as THREE.Mesh | undefined;
        const line = lineObject?.material as THREE.LineBasicMaterial | undefined;
        const core = coreObject?.material as THREE.MeshBasicMaterial | undefined;
        if (line) line.opacity = 0.95 * (1 - t);
        if (core) core.opacity = 0.86 * (1 - t);
      }
    }
    } catch {
      // Presentation must never abort the R3F render / physics loop.
    }
  });

  return (
    <group>
      {debrisGeometries.map((geometry, index) => (
        <instancedMesh
          key={index}
          ref={(node) => {
            debrisMeshes.current[index] = node;
          }}
          args={[geometry, debrisMat, Math.max(perVariant, 1)]}
          frustumCulled={false}
        />
      ))}
      <instancedMesh ref={streakMesh} args={[undefined, undefined, Math.max(streakCount, 1)]} frustumCulled={false}>
        <boxGeometry args={[1, 1, 1]} />
        <primitive object={streakMat} attach="material" />
      </instancedMesh>
      <instancedMesh ref={dustMesh} args={[dustGeometry, dustMat, Math.max(dustCount, 1)]} frustumCulled={false} renderOrder={8} raycast={() => null} />
      <group ref={shockGroup}>
        {Array.from({ length: shockCount }, (_, i) => (
          <mesh key={i} visible={false}>
            <torusGeometry args={[1, 0.04, 8, 48]} />
            <primitive object={ringMaterials[i]} attach="material" />
          </mesh>
        ))}
      </group>
      <group ref={burstGroup}>
        {Array.from({ length: burstCount }, (_, i) => (
          <group key={i} visible={false}>
            <lineSegments geometry={burstGeometry} material={burstLineMaterials[i]} />
            <mesh geometry={burstCoreGeometry} material={burstCoreMaterials[i]} />
          </group>
        ))}
      </group>
    </group>
  );
}
